import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  FileText,
  TrendingUp,
  Check,
  ChevronDown,
  Lock,
  Mail,
  ArrowRight,
  BookOpen,
  Award,
  Sparkles,
  AlertCircle,
  Gift,
  Download,
  Zap,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CONTACT_EMAIL = "info@tweakmycreditreport.com";
const EBOOK_COVER =
  "https://static.prod-images.emergentagent.com/jobs/10ada18c-7a58-44c6-ac8a-ae785b89c4b7/images/b82a254948a563e7c6ab95f901c74d5309a96c6c132cf88fbaa6debd0d9b9b4b.png";
const AUTHOR_HEADSHOT =
  "https://static.prod-images.emergentagent.com/jobs/10ada18c-7a58-44c6-ac8a-ae785b89c4b7/images/a22a2e70cfbe08dfe11b12c1bd4bea6386538e2c1b7f92729f552c2b92a99eb3.png";

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "DIY Credit Dispute Guide",
    desc: "Master the FCRA & e-OSCAR system the way bureaus actually process disputes — no guesswork, just the rules they don't want you to know.",
    bullets: ["FCRA section walkthroughs", "How e-OSCAR really works", "Validation vs. verification"],
  },
  {
    icon: FileText,
    title: "Dispute Letter Templates Library",
    desc: "Copy-paste ready letters for every scenario: late payments, charge-offs, collections, identity errors, and method-of-verification follow-ups.",
    bullets: ["100+ proven templates", "Bureau-specific addressing", "Escalation sequences"],
  },
  {
    icon: TrendingUp,
    title: "Reestablishing & Building Credit",
    desc: "The exact playbook a former mortgage broker uses to take a thin or damaged file to a mortgage-ready score in under 12 months.",
    bullets: ["Secured card stacking", "Authorized user strategy", "Utilization & age tactics"],
  },
];

const FAQS = [
  {
    q: "Is this legal? I've heard credit repair companies are sketchy.",
    a: "Yes — completely. Every consumer has the right under the Fair Credit Reporting Act (FCRA) to dispute inaccurate, unverifiable, or incomplete information on their credit report. This kit teaches you to exercise rights you already have. We are not a credit repair organization and do not act on your behalf.",
  },
  {
    q: "How long until I see results?",
    a: "Bureaus have 30 days (sometimes 45) to respond to disputes. Most readers see their first deletions within 30–60 days. A full file rebuild typically takes 3–6 months of consistent work using the kit's playbook.",
  },
  {
    q: "Will this work if I have collections, charge-offs, or a bankruptcy?",
    a: "Yes — the kit has dedicated chapters and letter templates for every major derogatory item, including collections, charge-offs, repossessions, late payments, and post-bankruptcy rebuilding.",
  },
  {
    q: "Do I get the kit immediately after purchase?",
    a: "Yes. As soon as your $29 payment is confirmed, you'll receive instant download access to the complete Founders Bundle — both the Ultimate Credit Repair Tool Kit and the Master Dispute Library.",
  },
  {
    q: "Is there a refund policy?",
    a: "Because this is a digital product delivered instantly, all sales are final. We're confident in the material — but please review the disclaimer at the bottom of this page before purchasing.",
  },
];

function FadeIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function Header({ onCheckout }) {
  return (
    <header
      className="tk-header sticky top-0 z-50"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" data-testid="brand-link">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
               style={{ background: "linear-gradient(135deg,#14B8A6,#0d9488)" }}>
            <ShieldCheck className="w-5 h-5 text-[#020813]" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold text-white text-base md:text-lg tracking-tight">
            tweakmycreditreport<span className="text-[#14B8A6]">.com</span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm text-[#94A3B8]">
          <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">What's Inside</a>
          <a href="#author" className="hover:text-white transition-colors" data-testid="nav-author">Author</a>
          <a href="#jumpstart" className="hover:text-[#FCD34D] transition-colors" data-testid="nav-jumpstart">Free Guide</a>
          <a href="#faq" className="hover:text-white transition-colors" data-testid="nav-faq">FAQ</a>
        </nav>
        <button
          onClick={onCheckout}
          className="btn-gold px-4 md:px-5 py-2.5 rounded-lg text-sm"
          data-testid="header-cta-button"
        >
          Download the Kit
        </button>
      </div>
    </header>
  );
}

function Hero({ onCheckout }) {
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="absolute inset-0 tk-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <FadeIn>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                   style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))",
                            border: "1px solid rgba(245,158,11,0.4)" }}
                   data-testid="hero-urgency-badge">
                <Zap className="w-3.5 h-3.5 text-[#FCD34D]" fill="#FCD34D" />
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FCD34D]">
                  Limited Time Founders Special — Save Over 70%
                </span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#14B8A6]/30 bg-[#14B8A6]/5 mb-7 ml-0 md:ml-3">
                <Sparkles className="w-3.5 h-3.5 text-[#14B8A6]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#14B8A6]">
                  Financial Freedom Starts Here
                </span>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-white">
                The Complete Founders Bundle:
                <span className="block mt-2 bg-clip-text text-transparent"
                      style={{ backgroundImage: "linear-gradient(135deg,#FCD34D 0%,#F59E0B 60%,#D97706 100%)" }}>
                  Take Control of Your Financial Future.
                </span>
              </h1>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-6 text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-xl">
                Get <span className="text-white font-semibold">The Ultimate Credit Repair Tool Kit</span> +{" "}
                <span className="text-white font-semibold">The Master Dispute Library</span> (100+ templates) —
                bundled together for our founders launch. Authored by a former mortgage broker. Everything
                you need to dispute errors, rebuild credit, and qualify for the life you deserve.
              </p>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-center">
                <button
                  onClick={onCheckout}
                  className="btn-gold px-7 py-4 rounded-xl text-base inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta-button"
                >
                  Get the Bundle · $29
                  <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href="#features"
                  className="btn-ghost px-6 py-4 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-2"
                  data-testid="hero-secondary-button"
                >
                  See What's Inside
                </a>
              </div>
            </FadeIn>
            <FadeIn delay={0.4}>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-[#94A3B8]">
                <span className="inline-flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-[#14B8A6]" /> Secure PayPal Checkout
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-[#14B8A6]" /> Instant PDF Download
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5 text-[#14B8A6]" /> Built by a Mortgage Industry Veteran
                </span>
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <FadeIn delay={0.2} className="relative">
              <div className="absolute -inset-10 rounded-full"
                   style={{ background: "radial-gradient(closest-side, rgba(245,158,11,0.18), transparent 70%)" }} />
              <div className="absolute -inset-6 rounded-full"
                   style={{ background: "radial-gradient(closest-side, rgba(20,184,166,0.18), transparent 70%)" }} />
              <div className="relative tk-float">
                <img
                  src={EBOOK_COVER}
                  alt="The Ultimate Credit Repair Tool Kit ebook cover"
                  className="relative w-[280px] sm:w-[340px] lg:w-[380px] rounded-xl shadow-[0_40px_80px_-20px_rgba(245,158,11,0.35)]"
                  data-testid="hero-ebook-image"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = ["FCRA Compliant Methods", "e-OSCAR Insider Insights", "Mortgage-Ready Strategies", "100+ Letter Templates"];
  return (
    <section className="border-y border-[#14B8A6]/10 bg-[#020813]/60">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5">
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-semibold">
          {items.map((it) => (
            <span key={it} className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#14B8A6]" /> {it}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <FadeIn>
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#14B8A6]">
              Inside The Tool Kit
            </span>
            <h2 className="font-display mt-3 text-3xl md:text-5xl font-black tracking-tighter text-white leading-tight">
              Three pillars. One blueprint to a mortgage-ready credit file.
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#94A3B8] leading-relaxed">
              Every tool, template, and tactic in the kit is built around one goal: turning your credit
              report from a roadblock into a launchpad.
            </p>
          </div>
        </FadeIn>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
          {FEATURES.map((f, idx) => (
            <FadeIn key={f.title} delay={0.1 * idx}>
              <div className="tk-card p-8 md:p-9 h-full" data-testid={`feature-card-${idx}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
                     style={{ background: "rgba(20,184,166,0.12)", border: "1px solid rgba(20,184,166,0.25)" }}>
                  <f.icon className="w-6 h-6 text-[#14B8A6]" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">
                  {f.title}
                </h3>
                <p className="mt-3 text-[#94A3B8] leading-relaxed text-sm md:text-base">{f.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {f.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-[#CBD5E1]">
                      <Check className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatsInside() {
  const items = [
    "Step-by-step credit report dismantling worksheet",
    "Dispute escalation flowchart (round 1 → litigation threat)",
    "Method-of-verification follow-up letter templates",
    "Goodwill, pay-for-delete, and 609 letter scripts",
    "Authorized-user strategy with vetted card list",
    "Secured-card → unsecured graduation timelines",
    "Utilization optimization calendar (mortgage-ready)",
    "Bonus: pre-mortgage 90-day score boost protocol",
  ];
  return (
    <section className="relative py-20 md:py-28 border-t border-[#14B8A6]/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <FadeIn className="lg:col-span-5">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#14B8A6]">
            Everything You Get
          </span>
          <h2 className="font-display mt-3 text-3xl md:text-4xl font-black tracking-tighter text-white">
            Not just an ebook — a complete operating manual.
          </h2>
          <p className="mt-5 text-[#94A3B8] leading-relaxed">
            Most "credit hack" guides give you theory. This kit gives you the exact steps,
            scripts, and sequences a mortgage professional uses to push borderline files over the line.
          </p>
        </FadeIn>
        <FadeIn delay={0.1} className="lg:col-span-7">
          <div className="tk-card p-8 md:p-10">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {items.map((it, i) => (
                <div key={it} className="flex items-start gap-3" data-testid={`whats-inside-${i}`}>
                  <span className="mt-1 w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.4)" }}>
                    <Check className="w-3 h-3 text-[#F59E0B]" strokeWidth={3} />
                  </span>
                  <span className="text-sm md:text-[15px] text-[#E2E8F0] leading-relaxed">{it}</span>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function AboutAuthor() {
  return (
    <section id="author" className="relative py-24 md:py-32 border-t border-[#14B8A6]/10">
      <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
        <FadeIn className="md:col-span-5">
          <div className="relative max-w-sm mx-auto md:mx-0">
            <div className="absolute -inset-4 rounded-2xl"
                 style={{ background: "radial-gradient(closest-side, rgba(20,184,166,0.25), transparent 70%)" }} />
            <img
              src={AUTHOR_HEADSHOT}
              alt="Author — former mortgage broker"
              className="relative rounded-2xl w-full object-cover aspect-[4/5] border border-[#14B8A6]/20"
              data-testid="author-headshot"
            />
          </div>
        </FadeIn>
        <FadeIn delay={0.1} className="md:col-span-7">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#14B8A6]">
            About the Author
          </span>
          <h2 className="font-display mt-3 text-3xl md:text-4xl font-black tracking-tighter text-white leading-tight">
            A credit industry insider on a mission to put the tools back in your hands.
          </h2>
          <p className="mt-6 text-[#CBD5E1] leading-relaxed">
            Before writing this kit, the author spent years inside the mortgage industry —
            sitting across from qualified, hard-working buyers who kept getting denied for one reason:
            a credit report nobody had taught them how to read, challenge, or rebuild.
          </p>
          <p className="mt-4 text-[#94A3B8] leading-relaxed">
            That experience turned into a mission. The playbook in this kit is built on deep expertise
            in the Fair Credit Reporting Act, the e-OSCAR dispute system, and the day-to-day realities of
            how bureaus, lenders, and underwriters actually make decisions. More than a set of tactics,
            it's a passion project: a belief that every consumer deserves the tools to take control of
            their financial future — not gatekept behind expensive "credit repair" companies, but owned
            outright.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#14B8A6]/30 text-[#14B8A6] bg-[#14B8A6]/5">
              Mortgage Industry Veteran
            </span>
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#F59E0B]/30 text-[#FCD34D] bg-[#F59E0B]/5">
              FCRA Practitioner
            </span>
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#94A3B8]/20 text-[#CBD5E1] bg-white/5">
              Consumer Advocate
            </span>
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="mt-6 inline-flex items-center gap-2 text-sm text-[#CBD5E1] hover:text-[#14B8A6] transition-colors group"
            data-testid="author-contact-email"
          >
            <Mail className="w-4 h-4 text-[#14B8A6]" />
            <span className="border-b border-transparent group-hover:border-[#14B8A6]">
              {CONTACT_EMAIL}
            </span>
          </a>
        </FadeIn>
      </div>
    </section>
  );
}

function LeadMagnet() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        email: trimmed,
        name: name.trim() || null,
        source: "jumpstart_guide",
      });
      setSuccess(true);
      toast.success("Check your inbox — your Jumpstart guide is on the way!");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't save your email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="jumpstart" className="relative py-24 md:py-32 border-t border-[#14B8A6]/10">
      <div className="absolute inset-0 tk-grid opacity-20 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <div className="tk-card overflow-hidden grid lg:grid-cols-12 gap-0 items-stretch"
             style={{ background: "linear-gradient(135deg, rgba(14,27,54,0.95) 0%, rgba(2,8,19,0.95) 100%)" }}>
          <div className="lg:col-span-7 p-8 md:p-12">
            <FadeIn>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#F59E0B]/30 bg-[#F59E0B]/5 mb-6">
                <Gift className="w-3.5 h-3.5 text-[#FCD34D]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FCD34D]">
                  Free Lead Magnet
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-white leading-tight">
                Not ready for the full kit?
                <span className="block mt-1 bg-clip-text text-transparent"
                      style={{ backgroundImage: "linear-gradient(135deg,#14B8A6 0%,#22d3ee 100%)" }}>
                  Grab the Credit Repair Jumpstart — free.
                </span>
              </h2>
              <p className="mt-5 text-[#94A3B8] leading-relaxed">
                A 12-page starter guide pulled straight from the full kit. Learn the 3 fastest FCRA
                disputes to file this week, the one sentence that forces bureaus to delete unverified
                items, and the mistake 90% of DIYers make that stalls their score for months.
              </p>
              <ul className="mt-6 space-y-2.5">
                {[
                  "3 quick-win disputes you can file tonight",
                  "The exact \"method of verification\" sentence bureaus fear",
                  "Free dispute tracker worksheet (printable)",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-[#E2E8F0]">
                    <Check className="w-4 h-4 text-[#14B8A6] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>

          <div className="lg:col-span-5 p-8 md:p-12 border-t lg:border-t-0 lg:border-l border-[#14B8A6]/10"
               style={{ background: "rgba(2,8,19,0.5)" }}>
            <FadeIn delay={0.15}>
              {success ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-8" data-testid="jumpstart-success">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                       style={{ background: "rgba(20,184,166,0.15)", border: "1px solid rgba(20,184,166,0.4)" }}>
                    <Check className="w-7 h-7 text-[#14B8A6]" strokeWidth={3} />
                  </div>
                  <h3 className="font-display text-xl font-black text-white">You're on the list.</h3>
                  <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">
                    Watch your inbox — the Credit Repair Jumpstart guide is on its way to{" "}
                    <span className="text-white">{email}</span>.
                  </p>
                  <p className="mt-4 text-[11px] text-[#64748B]">
                    Need help? Reach us at{" "}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#14B8A6] hover:underline">
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="h-full flex flex-col justify-center" data-testid="jumpstart-form">
                  <label htmlFor="lm-name" className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">
                    First name <span className="text-[#64748B] lowercase font-normal">(optional)</span>
                  </label>
                  <input
                    id="lm-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jamie"
                    className="tk-input mb-4"
                    data-testid="jumpstart-name-input"
                  />
                  <label htmlFor="lm-email" className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                    <input
                      id="lm-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="tk-input pl-11"
                      data-testid="jumpstart-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-gold mt-5 px-6 py-3.5 rounded-xl text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="jumpstart-submit-button"
                  >
                    {submitting ? "Sending…" : (<>Send Me the Free Guide <Download className="w-4 h-4" /></>)}
                  </button>
                  <p className="mt-3 text-[11px] text-[#64748B] text-center">
                    No spam. Unsubscribe anytime. Questions?{" "}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#94A3B8] hover:text-[#14B8A6]">
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                </form>
              )}
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingCTA({ onCheckout, email, setEmail, loading }) {
  return (
    <section id="pricing" className="relative py-24 md:py-32 border-t border-[#14B8A6]/10">
      <div className="absolute inset-0 hero-glow pointer-events-none opacity-60" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10">
        <FadeIn>
          <div className="tk-card p-8 md:p-12 text-center"
               style={{ background: "linear-gradient(180deg, rgba(14,27,54,0.95), rgba(2,8,19,0.95))" }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                 style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0.08))",
                          border: "1px solid rgba(245,158,11,0.45)" }}
                 data-testid="pricing-urgency-badge">
              <Zap className="w-3.5 h-3.5 text-[#FCD34D]" fill="#FCD34D" />
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FCD34D]">
                Limited Time Founders Special — Save Over 70%
              </span>
            </div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#14B8A6] block">
              Founders Launch · One-Time Purchase · Lifetime Access
            </span>
            <h2 className="font-display mt-4 text-3xl md:text-5xl font-black tracking-tighter text-white leading-tight">
              The Complete Founders Bundle.
              <span className="block mt-1" style={{ color: "#FCD34D" }}>Everything for $29.</span>
            </h2>
            <p className="mt-5 text-[#94A3B8] max-w-xl mx-auto leading-relaxed">
              Both products, one launch price. Less than the cost of one late fee — less than one hour of
              a credit-repair company's bill.
            </p>

            <div className="mt-7 max-w-md mx-auto grid grid-cols-1 sm:grid-cols-2 gap-3 text-left" data-testid="bundle-contents">
              <div className="flex items-start gap-3 p-3 rounded-lg"
                   style={{ background: "rgba(20,184,166,0.06)", border: "1px solid rgba(20,184,166,0.18)" }}>
                <Check className="w-4 h-4 text-[#14B8A6] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <div>
                  <div className="text-sm font-semibold text-white">The Ultimate Credit Repair Tool Kit</div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5">FCRA & e-OSCAR playbook</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg"
                   style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.22)" }}>
                <Check className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <div>
                  <div className="text-sm font-semibold text-white">The Master Dispute Library</div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5">100+ letter templates</div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-baseline justify-center gap-3">
              <span className="font-display text-6xl md:text-7xl font-black text-white" data-testid="pricing-amount">$29</span>
              <span className="text-lg text-[#94A3B8] line-through">$124</span>
              <span className="text-xs font-bold text-[#FCD34D] px-2 py-1 rounded-md ml-1"
                    style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.4)" }}>
                Save 76%
              </span>
            </div>

            <div className="mt-8 max-w-md mx-auto">
              <label htmlFor="cta-email" className="sr-only">Email</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <input
                    id="cta-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="tk-input pl-11"
                    data-testid="cta-email-input"
                  />
                </div>
                <button
                  onClick={onCheckout}
                  disabled={loading}
                  className="btn-gold px-6 py-3 rounded-xl text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="cta-checkout-button"
                >
                  {loading ? "Loading…" : (<>Get the Bundle · $29 <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
              <p className="mt-3 text-[11px] text-[#94A3B8] flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" /> Secure checkout via PayPal · Instant PDF delivery
              </p>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-2xl mx-auto pt-8 border-t border-[#14B8A6]/10">
              {[
                { v: "100+", l: "Letter Templates" },
                { v: "100%", l: "FCRA Compliant" },
                { v: "Instant", l: "PDF Download" },
              ].map((m) => (
                <div key={m.l}>
                  <div className="font-display text-2xl md:text-3xl font-black text-[#FCD34D]">{m.v}</div>
                  <div className="text-[11px] uppercase tracking-wider text-[#94A3B8] mt-1">{m.l}</div>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-24 md:py-32 border-t border-[#14B8A6]/10">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <FadeIn>
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#14B8A6]">FAQ</span>
            <h2 className="font-display mt-3 text-3xl md:text-4xl font-black tracking-tighter text-white">
              Common questions.
            </h2>
          </div>
        </FadeIn>
        <div className="space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <FadeIn key={f.q} delay={0.04 * i}>
                <div className="tk-card overflow-hidden" data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full text-left p-6 flex items-center justify-between gap-4"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-display text-base md:text-lg font-semibold text-white">{f.q}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-[#14B8A6] transition-transform duration-300 flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <div
                    className="px-6 transition-all duration-300 ease-in-out overflow-hidden"
                    style={{ maxHeight: isOpen ? 320 : 0, opacity: isOpen ? 1 : 0, paddingBottom: isOpen ? 24 : 0 }}
                  >
                    <p className="text-[#94A3B8] leading-relaxed text-sm md:text-base">{f.a}</p>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#14B8A6]/10" style={{ background: "#01040A" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg,#14B8A6,#0d9488)" }}>
                <ShieldCheck className="w-4 h-4 text-[#020813]" strokeWidth={2.5} />
              </div>
              <span className="font-display font-bold text-white tracking-tight">
                tweakmycreditreport<span className="text-[#14B8A6]">.com</span>
              </span>
            </div>
            <p className="mt-4 text-sm text-[#94A3B8] leading-relaxed max-w-md">
              Educational tools and templates to help consumers exercise their rights under the
              Fair Credit Reporting Act. Built by industry insiders. Owned by you.
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="mt-5 inline-flex items-center gap-2 text-sm text-[#CBD5E1] hover:text-[#14B8A6] transition-colors"
              data-testid="footer-contact-email"
            >
              <Mail className="w-4 h-4 text-[#14B8A6]" />
              {CONTACT_EMAIL}
            </a>
          </div>
          <div className="md:col-span-7">
            <h4 className="text-xs font-bold uppercase tracking-[0.25em] text-[#14B8A6] mb-3 flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5" /> Legal Disclaimer
            </h4>
            <p className="disclaimer">
              The information contained in <em>The Ultimate Credit Repair Tool Kit</em> and on
              tweakmycreditreport.com is provided for educational and informational purposes only. It is
              not intended as, and shall not be construed as, legal, financial, tax, or credit-counseling
              advice. tweakmycreditreport.com is not a credit repair organization (CRO) under the Credit
              Repair Organizations Act (CROA) and does not perform credit repair services on behalf of
              consumers. We make no guarantees, warranties, or representations regarding any specific
              credit score increase, dispute outcome, debt removal, or loan approval. Results vary
              based on individual circumstances, the accuracy of disputed information, and the actions
              taken by the consumer. You have the right under the Fair Credit Reporting Act (FCRA) to
              dispute inaccurate information on your credit reports directly with the credit bureaus
              free of charge. Consult a licensed attorney or financial professional for advice specific
              to your situation. By purchasing or using this kit, you acknowledge that you assume full
              responsibility for any actions you take based on its contents.
            </p>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-[#14B8A6]/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#64748B]">
          <span>© {new Date().getFullYear()} tweakmycreditreport.com — All rights reserved.</span>
          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-5">
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white transition-colors">{CONTACT_EMAIL}</a>
            <span className="hidden md:inline text-[#334155]">·</span>
            <div className="flex gap-5">
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function SuccessOverlay({ status, invoice, onClose }) {
  if (!status) return null;
  const isPaid = status === "paid";
  const downloadUrl = invoice ? `${API}/download/${invoice}` : null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 overflow-y-auto"
         style={{ background: "rgba(2,8,19,0.88)", backdropFilter: "blur(8px)" }}
         data-testid="payment-status-overlay">
      <div className="tk-card p-8 md:p-10 w-full max-w-md text-center my-auto relative">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors"
          data-testid="overlay-x-button"
        >
          <X className="w-4 h-4" />
        </button>
        {isPaid ? (
          <>
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
                 style={{ background: "rgba(20,184,166,0.15)", border: "1px solid rgba(20,184,166,0.4)" }}>
              <Check className="w-7 h-7 text-[#14B8A6]" strokeWidth={3} />
            </div>
            <h3 className="font-display mt-5 text-2xl md:text-3xl font-black text-white">Payment successful!</h3>
            <p className="mt-3 text-[#94A3B8] text-sm leading-relaxed">
              Welcome to the Founders Bundle. Click below to download your PDF — and keep an eye on
              your inbox for a backup link.
            </p>
            {downloadUrl ? (
              <a
                href={downloadUrl}
                download
                className="mt-6 btn-gold px-5 py-3 rounded-xl text-sm font-bold w-full inline-flex items-center justify-center gap-2"
                data-testid="download-pdf-button"
              >
                <Download className="w-4 h-4" /> Download Your PDF
              </a>
            ) : null}
            <button
              onClick={onClose}
              className="mt-3 btn-ghost px-5 py-2.5 rounded-lg text-xs font-semibold w-full"
              data-testid="overlay-close-button"
            >
              Close
            </button>
          </>
        ) : status === "checking" ? (
          <>
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
                 style={{ background: "rgba(20,184,166,0.15)" }}>
              <Sparkles className="w-7 h-7 text-[#14B8A6] animate-pulse" />
            </div>
            <h3 className="font-display mt-5 text-2xl font-black text-white">Confirming your payment…</h3>
            <p className="mt-3 text-[#94A3B8] text-sm">Hang tight — this only takes a moment.</p>
            <button
              onClick={onClose}
              className="mt-7 btn-ghost px-5 py-2.5 rounded-lg text-sm font-semibold w-full"
              data-testid="overlay-close-button"
            >
              Close
            </button>
          </>
        ) : status === "pending_confirmation" ? (
          <>
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
                 style={{ background: "rgba(245,158,11,0.15)" }}>
              <AlertCircle className="w-7 h-7 text-[#F59E0B]" />
            </div>
            <h3 className="font-display mt-5 text-2xl font-black text-white">Payment received — confirming</h3>
            <p className="mt-3 text-[#94A3B8] text-sm leading-relaxed">
              Your payment is being verified by PayPal. This usually completes within a few minutes.
              You'll receive an email with your download link shortly. If it takes longer than 15
              minutes, please email us at{" "}
              <a href="mailto:info@tweakmycreditreport.com" className="text-[#14B8A6] hover:underline">
                info@tweakmycreditreport.com
              </a>
              {" "}with your PayPal receipt.
            </p>
            <button
              onClick={onClose}
              className="mt-7 btn-ghost px-5 py-2.5 rounded-lg text-sm font-semibold w-full"
              data-testid="overlay-close-button"
            >
              Close
            </button>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
                 style={{ background: "rgba(245,158,11,0.15)" }}>
              <AlertCircle className="w-7 h-7 text-[#F59E0B]" />
            </div>
            <h3 className="font-display mt-5 text-2xl font-black text-white">Payment {status}</h3>
            <p className="mt-3 text-[#94A3B8] text-sm">
              Something went wrong or the session expired. Please try again, or email us at{" "}
              <a href="mailto:info@tweakmycreditreport.com" className="text-[#14B8A6] hover:underline">
                info@tweakmycreditreport.com
              </a>{" "}
              if you were charged.
            </p>
            <button
              onClick={onClose}
              className="mt-7 btn-ghost px-5 py-2.5 rounded-lg text-sm font-semibold w-full"
              data-testid="overlay-close-button"
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null | "checking" | "paid" | "expired" | "open" | "pending_confirmation"
  const [paymentInvoice, setPaymentInvoice] = useState(null);

  // Detect return from PayPal
  useEffect(() => {
    const url = new URL(window.location.href);
    const invoice = url.searchParams.get("paypal_invoice");
    const cancelled = url.searchParams.get("cancelled");
    if (cancelled) {
      toast.error("Checkout cancelled. Take your time — we'll be here.");
      url.searchParams.delete("cancelled");
      window.history.replaceState({}, "", url.toString());
      return;
    }
    if (invoice) {
      setPaymentInvoice(invoice);
      setPaymentStatus("checking");
      pollPaymentStatus(invoice, 0);
      url.searchParams.delete("paypal_invoice");
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollPaymentStatus = async (invoice, attempts) => {
    const maxAttempts = 15;
    if (attempts >= maxAttempts) {
      setPaymentStatus("pending_confirmation");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/checkout/paypal/status/${invoice}`);
      if (data.payment_status === "paid") {
        setPaymentStatus("paid");
        return;
      }
      if (data.status === "expired" || data.payment_status === "amount_mismatch") {
        setPaymentStatus("expired");
        return;
      }
      setTimeout(() => pollPaymentStatus(invoice, attempts + 1), 2500);
    } catch (e) {
      console.error(e);
      setTimeout(() => pollPaymentStatus(invoice, attempts + 1), 2500);
    }
  };

  const handleCheckout = async () => {
    if (loading) return;
    const trimmed = email.trim();
    setLoading(true);
    try {
      if (trimmed) {
        try {
          await axios.post(`${API}/leads`, { email: trimmed, source: "landing_page" });
        } catch (_) { /* ignore lead errors */ }
      }
      const { data } = await axios.post(`${API}/checkout/paypal`, {
        package_id: "founders_bundle",
        origin_url: window.location.origin,
        email: trimmed || undefined,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--tk-bg)" }}>
      <Header onCheckout={handleCheckout} />
      <main>
        <Hero onCheckout={handleCheckout} />
        <TrustBar />
        <Features />
        <WhatsInside />
        <AboutAuthor />
        <LeadMagnet />
        <PricingCTA onCheckout={handleCheckout} email={email} setEmail={setEmail} loading={loading} />
        <FAQ />
      </main>
      <Footer />
      <SuccessOverlay
        status={paymentStatus}
        invoice={paymentInvoice}
        onClose={() => { setPaymentStatus(null); setPaymentInvoice(null); }}
      />
    </div>
  );
}
